import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { ArrowRight } from 'lucide-react';
import { resolverDestinoRetomada } from '@/lib/retomadaDestino';
import { getQuadroLabel } from '@/lib/quadro-utils';

interface ItemLite {
  item_id: string;
}

interface Props {
  itens: ItemLite[];
}

// Mostra, por pedido, a fila EXATA pra onde a retomada vai mandar — usando a
// MESMA régua da retomada (resolverDestinoRetomada). Assim o que aparece é
// garantidamente o destino real. Compacto (só a coluna; quadro no tooltip).
export function DestinoRetomadaBadge({ itens }: Props) {
  const ids = (itens || []).map((i) => i.item_id).filter(Boolean);

  const { data, isLoading } = useQuery({
    queryKey: ['destino-retomada', [...ids].sort().join(',')],
    enabled: ids.length > 0,
    staleTime: 60_000,
    queryFn: async () => {
      const destinos: { coluna: string; quadroLabel: string }[] = [];
      for (const id of ids) {
        const d = await resolverDestinoRetomada(id);
        destinos.push({ coluna: d.coluna, quadroLabel: getQuadroLabel(d.quadro) });
      }
      // dedup por coluna+quadro
      const seen = new Set<string>();
      return destinos.filter((d) => {
        const k = `${d.quadroLabel}·${d.coluna}`;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    },
  });

  if (isLoading) {
    return <span className="text-[10px] text-muted-foreground">…</span>;
  }

  const destinos = data || [];
  if (!destinos.length) {
    return <span className="text-[10px] text-muted-foreground">—</span>;
  }

  return (
    <div className="flex flex-col gap-0.5 max-w-[130px]">
      {destinos.map((d) => (
        <Badge
          key={`${d.quadroLabel}·${d.coluna}`}
          variant="outline"
          title={`Volta para: ${d.quadroLabel} · ${d.coluna}`}
          className="text-[10px] gap-1 border-primary/40 bg-primary/5 text-primary font-medium max-w-full"
        >
          <ArrowRight className="h-2.5 w-2.5 shrink-0" />
          <span className="truncate">{d.coluna}</span>
        </Badge>
      ))}
    </div>
  );
}
